// ── Redux → template context/maps ────────────────────────────────────────────
// Small selectors turning the team slice into the shapes the resolver (import) and
// tokenizer (export) need. Kept independent of useDataPipeline so both the empty-
// dashboard loader and the admin export panel can use them cheaply.

import { useMemo } from "react";
import { useSelector } from "react-redux";
import type {
  MetricCategory,
  TemplateResolveContext,
} from "./templateTokens";

const METRIC_CATEGORIES: MetricCategory[] = [
  "gps", "testing", "forceplate", "wellness", "nutrition", "cognition",
];

interface TeamSlice {
  all_metric_types?: Record<string, Array<{ unique_identifier: string; name: string }>>;
  allPlayers?: Array<{ player_id: number | string; first_name?: string; last_name?: string; retired?: boolean }>;
  positionEntities?: Array<{ id: string | number; name: string }>;
  playerGroups?: Array<{ player_group_id: number | string; name: string }>;
}

function useTeam(): TeamSlice {
  return useSelector(({ team }: { team: TeamSlice }) => team);
}

function metricsByCategory(team: TeamSlice): TemplateResolveContext["metricsByCategory"] {
  const out = {} as TemplateResolveContext["metricsByCategory"];
  for (const cat of METRIC_CATEGORIES) {
    out[cat] = (team.all_metric_types?.[cat] ?? []).map((m) => ({ id: m.unique_identifier, name: m.name }));
  }
  return out;
}

function subjectPools(team: TeamSlice): TemplateResolveContext["subjects"] {
  return {
    player: (team.allPlayers ?? [])
      .filter((p) => !p.retired)
      .map((p) => ({ id: String(p.player_id), name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() })),
    position: (team.positionEntities ?? []).map((p) => ({ id: String(p.id), name: p.name })),
    group: (team.playerGroups ?? []).map((g) => ({ id: String(g.player_group_id), name: g.name })),
  };
}

/** Import side: build a resolver ctx for a given dashboard name. */
export function useTemplateResolveContext(): (dashboardName: string) => TemplateResolveContext {
  const team = useTeam();
  return useMemo(() => {
    const metrics = metricsByCategory(team);
    const subjects = subjectPools(team);
    return (dashboardName: string) => ({ metricsByCategory: metrics, subjects, dashboardName });
  }, [team]);
}

/** Export side: inverse lookups for the tokenizer. */
export function useTemplateExportMaps(): {
  metricCatalog: Map<string, { category: MetricCategory; name: string }>;
  subjectNames: Map<string, string>;
} {
  const team = useTeam();
  return useMemo(() => {
    const metricCatalog = new Map<string, { category: MetricCategory; name: string }>();
    for (const cat of METRIC_CATEGORIES) {
      for (const m of team.all_metric_types?.[cat] ?? []) {
        metricCatalog.set(m.unique_identifier, { category: cat, name: m.name });
      }
    }
    const subjectNames = new Map<string, string>();
    const pools = subjectPools(team);
    for (const p of pools.player) subjectNames.set(`player-${p.id}`, p.name);
    for (const p of pools.position) subjectNames.set(`pos-${p.id}`, p.name);
    for (const g of pools.group) subjectNames.set(`group-${g.id}`, g.name);
    return { metricCatalog, subjectNames };
  }, [team]);
}
